import { useEffect, useState } from 'react';
import api from '../../services/api';
import { Send, Check, X, Loader2, CalendarClock } from 'lucide-react';

const fmtDate = (d) => {
    if (!d) return null;
    const iso = d.length <= 10 ? d + 'T00:00:00' : d.replace(' ', 'T');
    const dt = new Date(iso);
    return isNaN(dt.getTime()) ? null : dt.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};
const isPast = (d) => {
    if (!d) return false;
    const iso = d.length <= 10 ? d + 'T23:59:59' : d.replace(' ', 'T');
    return new Date(iso).getTime() < Date.now();
};

/**
 * « Demander une prolongation » pour une thématique PARTAGÉE.
 *
 * Affiché par le parent uniquement quand l'accès approche/est expiré (< 7 j) —
 * au-delà, demander une prolongation n'a pas de sens. Ouvre un popup de
 * confirmation (contexte : partagé par X + échéance + dernière demande), puis
 * envoie une demande au propriétaire via /share/request-code.php (email + log +
 * quota 1 demande / jour).
 */
export default function ShareExtendRequest({ userThematiqueId, ownerName, accessEnd }) {
    const [open, setOpen] = useState(false);
    const [lastRequest, setLastRequest] = useState(null);
    const [canRequest, setCanRequest] = useState(true);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        if (!userThematiqueId) return;
        let cancelled = false;
        api.get('/share/request-code.php', { params: { user_thematique_id: userThematiqueId } })
            .then(({ data }) => {
                if (cancelled) return;
                setLastRequest(data?.data?.last_request || null);
                setCanRequest(data?.data?.can_request !== false);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [userThematiqueId]);

    useEffect(() => {
        if (!open) return;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, [open]);

    const send = async () => {
        if (sending || !canRequest) return;
        setSending(true);
        setErr('');
        try {
            const { data } = await api.post('/share/request-code.php', { user_thematique_id: userThematiqueId });
            setSent(true);
            setCanRequest(false);
            setLastRequest(data?.data?.sent_at || new Date().toISOString());
        } catch (e) {
            if (e.response?.status === 429) { setCanRequest(false); setErr("Tu as déjà envoyé une demande aujourd'hui — réessaie demain."); }
            else setErr(e.response?.data?.message || 'Erreur, réessaie.');
        } finally {
            setSending(false);
        }
    };

    const owner = ownerName || 'le propriétaire';
    const expired = isPast(accessEnd);

    return (
        <>
            <button className="sl-badge-extend" onClick={() => setOpen(true)}
                title={`Demander à ${owner} de prolonger ton accès`}>
                <Send size={14} /> Demander une prolongation
            </button>

            {open && (
                <div onClick={(e) => e.target === e.currentTarget && setOpen(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 9400, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div style={{ background: 'var(--card-bg)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, maxWidth: 440, width: '100%', boxShadow: '0 24px 48px rgba(0,0,0,0.3)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <CalendarClock size={20} style={{ color: 'var(--primary)' }} />
                            <h3 style={{ margin: 0, fontSize: '1.1em' }}>Prolonger ton accès</h3>
                            <button onClick={() => setOpen(false)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }} aria-label="Fermer"><X size={18} /></button>
                        </div>

                        <p style={{ fontSize: '0.92em', color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 12px' }}>
                            Cette thématique t'a été partagée par <strong style={{ color: 'var(--text)' }}>{owner}</strong>.
                            {accessEnd && <> Ton accès {expired ? 'a expiré' : 'arrive à échéance'} le <strong style={{ color: 'var(--text)' }}>{fmtDate(accessEnd)}</strong>.</>}
                        </p>
                        <p style={{ fontSize: '0.88em', color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 16px' }}>
                            L'accès partagé est piloté par les codes du propriétaire. Envoie-lui une demande : il recevra un email et pourra te renvoyer un code.
                        </p>

                        {sent ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, background: 'rgba(34,197,94,0.12)', color: 'var(--success, #22c55e)', fontSize: '0.9em' }}>
                                <Check size={16} /> Demande envoyée — {owner} a été notifié.
                            </div>
                        ) : (
                            <>
                                {lastRequest && (
                                    <p style={{ fontSize: '0.82em', color: 'var(--text-muted)', margin: '0 0 10px' }}>
                                        Dernière demande envoyée le {fmtDate(lastRequest)}.
                                    </p>
                                )}
                                {canRequest ? (
                                    <button className="btn btn-primary" onClick={send} disabled={sending} style={{ width: '100%', justifyContent: 'center' }}>
                                        {sending ? <Loader2 size={16} className="spin" /> : <Send size={16} />} Envoyer la demande à {owner}
                                    </button>
                                ) : (
                                    <div style={{ fontSize: '0.88em', color: 'var(--text-muted)', textAlign: 'center', padding: '8px 0' }}>
                                        Tu as déjà envoyé une demande aujourd'hui — réessaie demain.
                                    </div>
                                )}
                                {err && <p style={{ fontSize: '0.82em', color: 'var(--danger)', margin: '10px 0 0' }}>{err}</p>}
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
